import { useState } from 'react';
import { Link } from 'react-router-dom';
import { qs } from '../lib/api.js';
import { useResource } from '../lib/useApi.js';
import type { AuditEvent } from '../lib/types.js';
import { Card, Empty, ErrorState, Loading, Pill, When } from '../components/ui.js';

/**
 * Audit trail.
 *
 * Append-only on the server: nothing in the application updates or deletes a
 * row. Every template change and every deployment action lands here, which is
 * what makes "who changed the disclaimer" answerable months later.
 */
export function AuditLogPage(): JSX.Element {
  const [action, setAction] = useState('');
  const [search, setSearch] = useState('');
  const [actorUpn, setActorUpn] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);

  const events = useResource<{ events: AuditEvent[]; total: number }>(
    `/api/audit${qs({ action, search, actorUpn, take: 200 })}`,
  );
  const actions = useResource<{ actions: Array<{ action: string; count: number }> }>(
    '/api/audit/actions',
  );

  const exportUrl = '/api/audit/export.csv';

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Audit trail</h1>
          <p className="sub">
            Every template change and every deployment action, with the person, the time and what
            changed. Append-only — nothing here can be edited or removed.
          </p>
        </div>
        <div className="page-actions">
          <a className="btn" href={exportUrl} download>
            Export CSV
          </a>
        </div>
      </header>

      <div className="page-body">
        {events.error && <ErrorState error={events.error} onRetry={events.reload} />}

        <div className="toolbar">
          <select value={action} onChange={(e) => setAction(e.target.value)} style={{ maxWidth: 260 }}>
            <option value="">All actions</option>
            {(actions.data?.actions ?? []).map((a) => (
              <option key={a.action} value={a.action}>
                {a.action} ({a.count})
              </option>
            ))}
          </select>
          <input
            placeholder="Filter by person (UPN)"
            value={actorUpn}
            onChange={(e) => setActorUpn(e.target.value)}
            style={{ maxWidth: 220 }}
          />
          <input
            className="grow"
            placeholder="Search the summary"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {(action || actorUpn || search) && (
            <button
              onClick={() => {
                setAction('');
                setActorUpn('');
                setSearch('');
              }}
            >
              Clear
            </button>
          )}
          <span className="tiny muted">{events.data?.total ?? 0} events</span>
        </div>

        {events.loading && !events.data && <Loading rows={5} label="Loading audit trail" />}

        {events.data && events.data.events.length === 0 && (
          <Card>
            <Empty title="Nothing recorded yet">
              Audit entries appear as soon as somebody creates a template or runs a deployment.
            </Empty>
          </Card>
        )}

        {events.data && events.data.events.length > 0 && (
          <Card tight>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Who</th>
                    <th>Action</th>
                    <th>What happened</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {events.data.events.map((e) => (
                    <tr key={e.id}>
                      <td className="tiny muted nowrap" title={new Date(e.createdAt).toISOString()}>
                        <When iso={e.createdAt} />
                      </td>
                      <td className="tiny">
                        <div className="mono">{e.actorUpn}</div>
                        {e.actorRole && <Pill tone="outline">{e.actorRole.toLowerCase()}</Pill>}
                      </td>
                      <td className="tiny mono nowrap">{e.action}</td>
                      <td>
                        {e.summary}
                        <div className="tiny muted">
                          {e.entityType}
                          {e.entityId && (
                            <>
                              {' '}
                              {linkFor(e) ? (
                                <Link to={linkFor(e)!} className="mono">
                                  {e.entityId.slice(0, 10)}…
                                </Link>
                              ) : (
                                <span className="mono">{e.entityId.slice(0, 10)}…</span>
                              )}
                            </>
                          )}
                          {e.ip && <> · from {e.ip}</>}
                        </div>
                        {expanded === e.id && e.metadata && (
                          <pre className="log" style={{ marginTop: 6 }}>
                            {JSON.stringify(e.metadata, null, 2)}
                          </pre>
                        )}
                      </td>
                      <td className="right">
                        {e.metadata && (
                          <button
                            className="sm ghost"
                            onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                          >
                            {expanded === e.id ? 'Hide' : 'Detail'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {events.data && events.data.total > events.data.events.length && (
          <p className="tiny muted mt">
            Showing the most recent {events.data.events.length} of {events.data.total}. Narrow the
            filters, or export the CSV for the full record.
          </p>
        )}
      </div>
    </>
  );
}

/** Deep-links an audit row to the thing it happened to, where one exists. */
function linkFor(event: AuditEvent): string | null {
  if (!event.entityId) return null;
  switch (event.entityType) {
    case 'template':
      return `/templates/${event.entityId}`;
    case 'deployment':
      return `/deployments/${event.entityId}`;
    default:
      // Versions and rules live inside a template page, which the id alone does
      // not identify, so there is nothing honest to link to.
      return null;
  }
}
